import type {
  ListGroupsOptions,
  ListServicePrincipalsOptions,
} from '@ns/plugin-msgraph-proxy-client';

import type { GroupsQuery, ServicePrincipalsQuery } from './use-directory';

/** The Graph version both fixed queries request, and the only one whose page context is trusted. */
export const GRAPH_VERSION = 'v1.0' as const;

/** Stable ChoiceField source identity for every Entra group search term. */
export const GROUP_DIRECTORY_SOURCE_KEY = 'entra:groups';

/** Stable ChoiceField source identity for every Entra service-principal search term. */
export const SERVICE_PRINCIPAL_DIRECTORY_SOURCE_KEY =
  'entra:service-principals';

/** The debounced directory search term a list request is built from. */
export interface DirectorySearch {
  /** The settled visible input, including the empty first-page term. */
  readonly term: string;
}

/** The group properties every group request selects, so a page and a lookup by id build the same options. */
const GROUP_SELECT = 'id,displayName';

/** The service-principal properties every service-principal request selects, so a page and a lookup by id build the same options. */
const SERVICE_PRINCIPAL_SELECT = 'id,appId,displayName,servicePrincipalType';

/** The shape of a Graph directory object id: a GUID in 8-4-4-4-12 hexadecimal digits. */
const GRAPH_OBJECT_ID_PATTERN =
  /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;

/**
 * Whether a value has the shape of a Graph directory object id.
 *
 * @remarks
 * A lookup by id writes the value into an OData `$filter` between quotes, and
 * {@link buildGroupLookupQuery} and {@link buildServicePrincipalLookupQuery}
 * do not check it themselves. The validators run this check first: a GUID holds
 * only hexadecimal digits and hyphens, so no quote, space or operator.
 *
 * @param value - The id from submitted form data, which may come from a URL.
 * @returns Whether the value is a GUID, in either letter case.
 */
export function isGraphObjectId(value: string): boolean {
  return GRAPH_OBJECT_ID_PATTERN.test(value);
}

/**
 * Builds Graph's `$search` clause for a displayName search.
 *
 * @remarks
 * Graph reads the clause as a quoted phrase: without the quotes it rejects the
 * colon after `displayName` with a 400 syntax error, and inside them a
 * backslash or a double quote in the term must be escaped with a backslash. A
 * term that is empty after trimming is no search at all, because Graph rejects
 * a clause without a value.
 *
 * @param term - The settled text the filler typed.
 * @returns The quoted clause, or undefined when there is nothing to search for.
 */
export function displayNameSearch(term: string): string | undefined {
  const trimmed = term.trim();
  if (trimmed === '') return undefined;
  const escaped = trimmed.replaceAll(/[\\"]/g, String.raw`\$&`);
  return `"displayName:${escaped}"`;
}

/**
 * Builds the complete fixed request options for one group collection view.
 *
 * @remarks
 * The advanced-query pair (ConsistencyLevel: eventual plus $count=true) is
 * always sent: Graph refuses $orderby on the unfiltered groups list without
 * it (400 Request_UnsupportedQuery), and the empty term is the first request
 * every form makes.
 *
 * @param search - The settled term, which adds the $search clause only when it holds more than whitespace.
 * @returns The complete closed group request options.
 */
export function groupListOptions(
  search: DirectorySearch,
): Pick<
  ListGroupsOptions,
  | 'consistencyLevel'
  | 'count'
  | 'orderby'
  | 'resilience'
  | 'search'
  | 'select'
  | 'top'
  | 'version'
> {
  const fixed = {
    consistencyLevel: 'eventual' as const,
    count: true,
    orderby: 'displayName',
    resilience: { timeout: 30_000 },
    select: GROUP_SELECT,
    top: 999,
    version: GRAPH_VERSION,
  };
  const clause = displayNameSearch(search.term);
  return clause === undefined ? fixed : { ...fixed, search: clause };
}

/**
 * Builds the complete fixed request options for one service-principal collection view.
 *
 * @remarks
 * The advanced-query pair is always sent: Graph refuses sorting service
 * principals without it ("Sorting not supported for 'ServicePrincipal'").
 *
 * @param search - The settled term, which adds the $search clause only when it holds more than whitespace.
 * @returns The complete closed service-principal request options.
 */
export function servicePrincipalListOptions(
  search: DirectorySearch,
): Pick<
  ListServicePrincipalsOptions,
  | 'consistencyLevel'
  | 'count'
  | 'orderby'
  | 'resilience'
  | 'search'
  | 'select'
  | 'top'
  | 'version'
> {
  const fixed = {
    consistencyLevel: 'eventual' as const,
    count: true,
    orderby: 'displayName',
    resilience: { timeout: 30_000 },
    select: SERVICE_PRINCIPAL_SELECT,
    top: 100,
    version: GRAPH_VERSION,
  };
  const clause = displayNameSearch(search.term);
  return clause === undefined ? fixed : { ...fixed, search: clause };
}

/**
 * Builds the request options that ask one collection for exactly one object id.
 *
 * @remarks
 * It selects the same properties as the page, so the option builders read the
 * answer as they read a page. It sends no `$search`, `$orderby` or `$top`: the
 * filter names one object. Graph answers an id that names no object with a
 * 404 and the provider code `Request_ResourceNotFound`, not with an empty list.
 */
function buildLookupOptions(id: string, select: string): GroupsQuery {
  return {
    filter: `id eq '${id}'`,
    resilience: { timeout: 30_000 },
    select,
    version: GRAPH_VERSION,
  };
}

/**
 * What a lookup of one group by id asks Graph.
 *
 * @param id - A value that passed {@link isGraphObjectId}, because it is written into the `$filter`.
 * @returns The query for `buildGroupsQueryOptions`.
 */
export function buildGroupLookupQuery(id: string): GroupsQuery {
  return buildLookupOptions(id, GROUP_SELECT);
}

/**
 * What a lookup of one service principal by id asks Graph.
 *
 * @param id - A value that passed {@link isGraphObjectId}, because it is written into the `$filter`.
 * @returns The query for `buildServicePrincipalsQueryOptions`.
 */
export function buildServicePrincipalLookupQuery(
  id: string,
): ServicePrincipalsQuery {
  return buildLookupOptions(id, SERVICE_PRINCIPAL_SELECT);
}
